import { logger } from '@mail-core/logger';
import type { PayloadAction } from '@reduxjs/toolkit';
import config from 'Cloud/config';
import { getDomainFoldersApiCall } from 'reactApp/api/DomainFoldersApiCall';
import { itemV4ToV2 } from 'reactApp/api/helpers/apiV4Helpers';
import { IS_B2B_BIZ_USER, IS_BLOCKED, IS_ONPREMISE, IS_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import { loadDomainFoldersSuccess } from 'reactApp/modules/domainFolders/domainFolders.actions';
import { getClosestDomainFolder } from 'reactApp/modules/domainFolders/domainFolders.selectors';
import { loadFolderSuccess } from 'reactApp/modules/home/home.actions';
import type { ApiFolderResponse } from 'reactApp/modules/home/home.types';
import { loggerSaga } from 'reactApp/modules/logger/logger.saga';
import { addFilesSuccess } from 'reactApp/modules/modifying/modifying.actions';
import type { AddFileSuccessAction } from 'reactApp/modules/modifying/modifying.types';
import { routerDomainFolder, routeStatusPage } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import type { IRouteDomainFolder } from 'reactApp/modules/router/router.types';
import { searchLoadRequestStart } from 'reactApp/modules/search/search.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { EStatus } from 'reactApp/sections/ErrorPage/ErrorPage.types';
import { call, put, select, takeEvery } from 'redux-saga/effects';

export const domainFoldersApiCall = (path: string) => getDomainFoldersApiCall().makeRequest({ path });

export function* setDomainFolders() {
    const domainFolders = config.get('serverSideDomainFolders');

    try {
        const { list } = itemV4ToV2({ list: domainFolders });
        yield put(loadDomainFoldersSuccess(list));
    } catch (err) {
        logger.error(err);
    }
}

export function* getDomainFoldersList(path = '/') {
    const { data } = yield call(domainFoldersApiCall, path);

    try {
        return itemV4ToV2({ list: data?.body });
    } catch (error) {
        yield call(loggerSaga, { error, action: routeStatusPage({ status: EStatus.NOT_FOUND }) });
    }
}

function* loadDomainFolders(id = '/') {
    if (!IS_B2B_BIZ_USER || IS_PUBLIC || config.get('STATIC') || IS_BLOCKED) {
        return;
    }

    try {
        const { list } = yield call(getDomainFoldersList, id);
        yield put(loadDomainFoldersSuccess(list));
    } catch (error) {
        logger.error(error);
    }
}

export function* handleAddFileSuccess(action: PayloadAction<AddFileSuccessAction>) {
    const { parent } = action.payload;
    if (yield select(getClosestDomainFolder, parent)) {
        yield call(loadDomainFolders, parent);
    }
}

export function* handleRouterDomainFolder(action: PayloadAction<IRouteDomainFolder>) {
    const id = action.payload.id;
    yield call(loadDomainFolders, id);
}

export function* handleLoadFolderSuccess(action: PayloadAction<ApiFolderResponse>) {
    const { home } = action.payload;
    const storage = yield select(getCurrentStorage);
    const isMyOfficeEditor = storage === EStorageType.myoffice;

    if (isMyOfficeEditor && IS_ONPREMISE) {
        yield call(loadDomainFolders);
    }

    if (yield select(getClosestDomainFolder, home)) {
        yield call(loadDomainFolders, home);
    }
}

export function* handleSearchLoadRequestStart() {
    yield call(loadDomainFolders);
}

export function* watchDomainFolders() {
    yield takeEvery(addFilesSuccess.toString(), handleAddFileSuccess);
    yield takeEvery(loadFolderSuccess.toString(), handleLoadFolderSuccess);
    yield takeEvery(searchLoadRequestStart.toString(), handleSearchLoadRequestStart);
    yield takeEvery(routerDomainFolder.toString(), handleRouterDomainFolder);
}
